import { useEffect, useRef, useState } from "react";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { Keypad, keypadDigitsToMinor, useKeypadKeys } from "./Keypad";
import { maxTipMinor } from "./cart";

/**
 * "+ Tip" (D-063): the amount a customer adds on top of the goods, or leaves as change. Entered on the
 * Sell keypad, or typed on a hardware keyboard (see useKeypadKeys). The tip rides on the sale being built;
 * nothing is recorded until the sale completes.
 */
export function TipSheet(props: {
  /** The tip already on the cart, or 0 when adding one. */
  initialMinor: number;
  goodsTotalMinor: number;
  currency: CurrencyCode;
  /** Called with the new tip; 0 removes it. */
  onSave: (tipMinor: number) => void;
  onCancel: () => void;
}) {
  const [digits, setDigits] = useState(props.initialMinor > 0 ? String(props.initialMinor) : "");
  const tip = keypadDigitsToMinor(digits);
  const max = maxTipMinor(props.goodsTotalMinor);
  const tooBig = tip > max;
  const editing = props.initialMinor > 0;
  const canSave = tip > 0 && !tooBig;

  const save = () => {
    if (canSave) props.onSave(tip);
  };
  useKeypadKeys({ setDigits, onEnter: save, onEscape: props.onCancel });

  // Focus the dialog so keyboard users start inside it, not on "+ Tip" behind it.
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    dialog.current?.focus();
  }, []);

  return (
    <section ref={dialog} tabIndex={-1} className="sell__sheet sell__sheet--fit" role="dialog" aria-modal="true" aria-label="Tip">
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">Tip</h2>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={props.onCancel}>
          Cancel
        </button>
      </header>

      <div className="sell__sheet-body sell__sheet-body--fit">
        <div className="sell__tip">
          <div className="sell__tip-stats">
            <div className="sell__stat">
              <div className="sell__stat-label">Tip</div>
              <div className="sell__amount sell__amount--change" data-testid="tip-amount">
                {formatMinor(tip, props.currency)}
              </div>
            </div>
            <p className={tooBig ? "sell__warn" : "sell__note"} data-testid="tip-due" role={tooBig ? "alert" : undefined}>
              {tooBig
                ? `A tip can be at most ${formatMinor(max, props.currency)} on this sale.`
                : `Total with tip ${formatMinor(props.goodsTotalMinor + tip, props.currency)}`}
            </p>
          </div>
          <Keypad className="sell__tip-keypad" digits={digits} onDigits={setDigits} />
        </div>
      </div>

      <footer className="sell__sheet-foot">
        {editing ? (
          <div className="sell__tip-actions">
            <button type="button" className="sell__btn sell__btn--quiet sell__btn--tall" onClick={() => props.onSave(0)}>
              Remove tip
            </button>
            <button type="button" className="sell__btn sell__btn--block sell__btn--tall" disabled={!canSave} onClick={save}>
              Save tip
            </button>
          </div>
        ) : (
          <button type="button" className="sell__btn sell__btn--block sell__btn--tall" disabled={!canSave} onClick={save}>
            Add tip
          </button>
        )}
      </footer>
    </section>
  );
}
